import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Coins, X, Ban, Undo2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ExcludedNotFixedRate({ rows, onUndo, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="px-3 py-2 rounded border border-sky-500/30 bg-sky-500/5 text-[11px] text-muted-foreground" data-testid={testId}>
      <span className="font-semibold text-sky-300">Not fixed-rate (kept out):</span>{' '}
      {rows.map((r) => (
        <span key={r.ticker} className="inline-flex items-center gap-1 mr-2" data-testid={`${testId}-${r.ticker}`}>
          <span className="font-mono font-bold">{r.ticker}</span>
          <span className="text-muted-foreground/70">· {r.reason}</span>
          {r.reason === 'admin' && onUndo && (
            <button type="button" onClick={() => onUndo(r.ticker)} title="Undo — allow fixed-rate tagging again"
              className="p-0.5 rounded hover:bg-muted/40" data-testid={`${testId}-undo-${r.ticker}`}>
              <Undo2 className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

export async function markNotFixedRate(ticker, flag = true) {
  const { data } = await axios.post(`${API}/admin/not-fixed-rate/${ticker}`, { flag }, { withCredentials: true });
  return data;
}

export { ExcludedNotFixedRate };

/**
 * AdminTagBabyBondsButton — Round 93i (Jun 2026), refined Round 93m.
 *
 * Scans stock_cache for fixed-rate assets (baby bonds, preferred shares,
 * preferred units, mortgage bonds) via regex on the issuer name. Tagged
 * rows get `asset_class`, `par_value`, parsed `coupon_rate`, etc. so the
 * `_apply_baby_bond_override` runtime kicks in.
 *
 * Round 93m UX: every matched ticker now has a CHECKBOX next to it.
 *  - UNCHECKED (default) = this ticker WILL be tagged on commit
 *  - CHECKED              = SKIP this ticker (admin sanity-override
 *                           for false positives the regex may produce)
 * The commit POST sends a `skip_tickers` array of the boxes the admin
 * checked; the backend honours that list.
 *
 * Idempotent — re-running shows 0 newly-tagged + N already-tagged.
 */
export default function AdminTagBabyBondsButton() {
  // Round P111u — self-gate; hides even if wrapper is bypassed.
  const { user: _authUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState(null);
  // Tickers the admin EXPLICITLY checked to SKIP. Default = nothing
  // skipped (every match tags on commit).
  const [skipSet, setSkipSet] = useState(() => new Set());

  const openModal = async () => {
    setOpen(true);
    setError(null);
    setCommitResult(null);
    setSkipSet(new Set());
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/tag-baby-bonds?dry_run=true`, null, { withCredentials: true });
      setDryRunResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSkip = (ticker) => {
    setSkipSet((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) {
        next.delete(ticker);
      } else {
        next.add(ticker);
      }
      return next;
    });
  };

  const runCommit = async () => {
    setLoading(true);
    setError(null);
    try {
      const skip_tickers = Array.from(skipSet);
      const r = await axios.post(
        `${API}/admin/tag-baby-bonds`,
        { skip_tickers },
        { withCredentials: true },
      );
      setCommitResult(r.data);
      const skipped = (r.data.skipped_by_user || []).length;
      const tagged = r.data.tagged_count ?? 0;
      toast.success(
        skipped > 0
          ? `Tagged ${tagged} · Skipped ${skipped} per your selection`
          : `Tagged ${tagged} fixed-rate asset${tagged === 1 ? '' : 's'}`,
      );
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Tag commit failed');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setDryRunResult(null);
    setCommitResult(null);
    setError(null);
    setSkipSet(new Set());
  };

  const neverTag = async (t) => {
    try {
      await markNotFixedRate(t.ticker, true);
      setDryRunResult((prev) => prev && ({
        ...prev,
        tagged: (prev.tagged || []).filter((x) => x.ticker !== t.ticker),
        tagged_count: Math.max(0, (prev.tagged_count ?? 1) - 1),
        excluded_not_fixed_rate: [...(prev.excluded_not_fixed_rate || []), { ticker: t.ticker, name: t.name, reason: 'admin', admin_flag: true }],
      }));
      toast.success(`${t.ticker} marked not fixed-rate`, { description: 'Removed from tagging and Preferred Terms for good.' });
    } catch (e) {
      toast.error(`Could not flag ${t.ticker}`, { description: e?.response?.data?.detail || e?.message });
    }
  };

  const undoNotFixedRate = async (ticker) => {
    try {
      await markNotFixedRate(ticker, false);
      setDryRunResult((prev) => prev && ({
        ...prev,
        excluded_not_fixed_rate: (prev.excluded_not_fixed_rate || []).filter((x) => x.ticker !== ticker),
      }));
      toast.success(`${ticker} flag lifted — re-open to re-scan`);
    } catch (e) {
      toast.error(`Could not undo ${ticker}`, { description: e?.response?.data?.detail || e?.message });
    }
  };

  const tagged = dryRunResult?.tagged || [];
  const taggedCount = dryRunResult?.tagged_count ?? null;
  const willTagCount = useMemo(
    () => tagged.filter((t) => !skipSet.has(t.ticker)).length,
    [tagged, skipSet],
  );

  const formatCoupon = (rate) => {
    if (rate === null || rate === undefined) return 'board-set';
    const pct = rate * 100;
    return `${pct.toFixed(pct % 1 ? 3 : 2)}%`;
  };

  const labelForClass = (cls) => {
    if (cls === 'preferred_share') return 'Preferred';
    if (cls === 'preferred_unit') return 'Pref Unit';
    return 'Baby Bond';
  };

  // Round P111u — self-gate (post-hooks; safe with Rules of Hooks).
  if (_authUser?.role !== 'admin') return null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="admin-tag-baby-bonds-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-amber-500/15 hover:bg-amber-500/25 text-amber-900 dark:text-amber-200 border border-amber-500/40 transition-colors"
      >
        <Coins className="w-4 h-4" />
        Tag fixed-rate assets
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={close}
          data-testid="admin-tag-baby-bonds-modal"
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-card border border-amber-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-card border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Tag fixed-rate assets in stock_cache</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Baby bonds, preferred shares, preferred units, mortgage bonds. Check a row to SKIP it if the regex looks wrong.
                </p>
              </div>
              <button onClick={close} aria-label="Close" data-testid="admin-tag-baby-bonds-close" className="p-1 rounded hover:bg-muted/40">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="admin-tag-baby-bonds-loading">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Working…
                </div>
              )}

              {error && (
                <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200" data-testid="admin-tag-baby-bonds-error">{error}</div>
              )}

              {dryRunResult && !commitResult && (
                <>
                  <div className="text-sm" data-testid="admin-tag-baby-bonds-summary">
                    Found <span className="font-mono font-bold text-amber-200">{taggedCount}</span> untagged match{taggedCount === 1 ? '' : 'es'} in stock_cache.{' '}
                    {skipSet.size > 0 && (
                      <span className="text-muted-foreground">
                        ({skipSet.size} skipped · <span className="text-amber-200">{willTagCount} will tag</span>)
                      </span>
                    )}
                  </div>
                  {tagged.length > 0 && (
                    <div className="border border-border/60 rounded-md overflow-hidden">
                      <div className="px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-between">
                        <span>Matches (check box to SKIP):</span>
                        <span className="normal-case text-muted-foreground/80">
                          {willTagCount} of {tagged.length} will tag
                        </span>
                      </div>
                      <div className="max-h-72 overflow-y-auto p-2 space-y-1">
                        {tagged.map((t) => {
                          const isSkipped = skipSet.has(t.ticker);
                          return (
                            <label
                              key={t.ticker}
                              className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                                isSkipped
                                  ? 'bg-rose-500/10 hover:bg-rose-500/15'
                                  : 'hover:bg-muted/30'
                              }`}
                              data-testid={`admin-tag-baby-bonds-row-${t.ticker}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSkipped}
                                onChange={() => toggleSkip(t.ticker)}
                                data-testid={`admin-tag-baby-bonds-skip-${t.ticker}`}
                                className="mt-0.5 w-3.5 h-3.5 rounded border-border accent-rose-500 cursor-pointer"
                                title={isSkipped ? 'Currently SKIPPED — uncheck to include' : 'Check to SKIP this ticker'}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-1.5">
                                  <span className={`font-mono font-bold ${isSkipped ? 'text-rose-200 line-through' : 'text-amber-200'}`}>
                                    {t.ticker}
                                  </span>
                                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground/80">
                                    {labelForClass(t.asset_class)}
                                  </span>
                                  <span className={isSkipped ? 'text-muted-foreground/70' : 'text-muted-foreground'}>
                                    {formatCoupon(t.coupon_rate)}
                                    {t.par_value != null ? ` on $${t.par_value} par` : ''}
                                    {t.bond_maturity_year ? ` · due ${t.bond_maturity_year}` : ''}
                                  </span>
                                  {isSkipped && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-rose-300">
                                      will skip
                                    </span>
                                  )}
                                </div>
                                <div className={`text-[10px] pl-0.5 truncate ${isSkipped ? 'text-muted-foreground/40 line-through' : 'text-muted-foreground/60'}`}>
                                  {t.name}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); neverTag(t); }}
                                title="Not a fixed-rate asset — never tag, remove from Preferred Terms"
                                data-testid={`admin-tag-baby-bonds-never-${t.ticker}`}
                                className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-sky-300 border border-sky-500/40 hover:bg-sky-500/15"
                              >
                                <Ban className="w-3 h-3" /> Not fixed-rate
                              </button>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <ExcludedNotFixedRate rows={dryRunResult.excluded_not_fixed_rate} onUndo={undoNotFixedRate} testId="admin-tag-baby-bonds-excluded" />
                  {tagged.length > 0 && (
                    <button
                      type="button"
                      onClick={runCommit}
                      disabled={loading || willTagCount === 0}
                      data-testid="admin-tag-baby-bonds-commit"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/40 disabled:opacity-50 transition-colors"
                    >
                      <Coins className="w-4 h-4" />
                      {willTagCount === 0
                        ? 'Nothing to tag — all skipped'
                        : `Tag ${willTagCount} now${skipSet.size > 0 ? ` (skipping ${skipSet.size})` : ''}`}
                    </button>
                  )}
                  {tagged.length === 0 && (
                    <div className="text-xs text-muted-foreground">stock_cache is already in sync. Nothing to tag.</div>
                  )}
                </>
              )}

              {commitResult && (
                <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/40 text-sm text-emerald-200 space-y-1" data-testid="admin-tag-baby-bonds-commit-result">
                  <div>✅ Tagged {commitResult.tagged_count} fixed-rate asset{commitResult.tagged_count === 1 ? '' : 's'} successfully.</div>
                  {(commitResult.skipped_by_user || []).length > 0 && (
                    <div className="text-xs text-amber-200">
                      Skipped per your selection: {(commitResult.skipped_by_user || []).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
